import { api } from '@racepointenergy/swc/client/api'
import { configureClient } from '@racepointenergy/swc/scs/client'
import { createSlice } from '@reduxjs/toolkit'

import history from 'app/history'
import { makeFetchSlice, fetchDataThunk } from 'app/redux-fetch'
import { SCS_AGENT, SCS_CHANNEL } from 'app/consts'

import { USER_REDUCER_NAME } from './consts'
import { AppDispatch } from 'app/store'
import { resetStore } from 'app/store/actions'
import { authorizeBillingAxios } from 'app/axios/billing'
import { authorizeEdgeAxios } from 'app/axios/edge'
import { getUserSelf, passwordReset } from './api'

import {
  clearAuthorizedUser,
  getAuthorizedUser,
} from 'app/services/LocalStorage'
import { authorizeConfigUploadAxios } from 'app/axios/upload'

interface PassInitResetActionParams {
  email: string
  onSuccess: () => void
}

const passInitResetSlice = makeFetchSlice('passInitReset')
export const passInitResetReducer = passInitResetSlice.reducer
export const passInitResetActions = passInitResetSlice.actions

const userSlice = createSlice({
  initialState: {
    user: null,
    hasLoaded: false,
  },
  name: USER_REDUCER_NAME,
  reducers: {
    authorize: (_, action) => ({ hasLoaded: true, user: action.payload }),
    unauthorize: () => ({ hasLoaded: false, user: null }),
  },
})
export const userReducer = userSlice.reducer
export const userActions = userSlice.actions

export const logOutThunk = () => {
  return (dispatch: AppDispatch) => {
    // References CENTRAL-764
    // First, clear out cache state to hang up on active http and websocket sockets
    dispatch(api.util.resetApiState())
    // Then, remove associated client data for cache state
    dispatch(configureClient({ userId: '', secretKey: '', token: '' }))
    // Finally, reset all state unless allow listed
    dispatch(resetStore())
    clearAuthorizedUser()
    dispatch(userActions.unauthorize())
    history.replace('/', null)
  }
}

export const updateAuthStatusThunk = () => {
  return async (dispatch: AppDispatch) => {
    const storedUser = getAuthorizedUser()
    if (storedUser) {
      const authorizeData = {
        token: storedUser.token || '',
        secretKey: storedUser.secretKey || '',
        onUnauthorized: () => dispatch(logOutThunk()),
      }
      await authorizeBillingAxios(authorizeData)
      await authorizeEdgeAxios(authorizeData)
      await authorizeConfigUploadAxios(authorizeData)

      const userSelf = await getUserSelf()

      dispatch(
        configureClient({
          secretKey: storedUser.secretKey || '',
          token: storedUser.token || '',
          userId: storedUser.userId || '',
          agent: SCS_AGENT,
          channel: SCS_CHANNEL,
        })
      )
      dispatch(userActions.authorize(userSelf?.data?.payload))
    }
  }
}

export const passInitResetThunk = ({
  email,
  onSuccess,
}: PassInitResetActionParams) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchDataThunk({
        dispatch,
        actions: passInitResetActions,
        asyncFunc: () => passwordReset({ email }),
      })

      onSuccess()

      return response
    } catch (err) {
      console.log('Error initiating password reset', err)
    }
  }
}
